/**
 * Chat API client — always pass teamId; user identity comes from JWT (axiosInstance).
 * Do not send userId for authorization (optional echo fields in responses for UI/cache only).
 */
import axiosInstance from 'shared/services/api/axiosInstance';

function assertTeamId(teamId) {
  const id = Number(teamId);
  if (!id || Number.isNaN(id)) {
    throw new Error('teamId is required for chat');
  }
  return id;
}

export async function listChatSessions(teamId, params = {}) {
  const id = assertTeamId(teamId);
  const { data } = await axiosInstance.get('/chat/sessions', {
    params: { teamId: id, ...params },
  });
  return data;
}

export async function createChatSession(teamId, body = {}) {
  const id = assertTeamId(teamId);
  const { data } = await axiosInstance.post('/chat/sessions', { teamId: id, ...body });
  return data;
}

export async function getCurrentChatSession(teamId) {
  const id = assertTeamId(teamId);
  const { data } = await axiosInstance.get('/chat/sessions/current', {
    params: { teamId: id },
  });
  return data;
}

export async function getChatMessages(sessionId, teamId, params = {}) {
  const tid = assertTeamId(teamId);
  const { data } = await axiosInstance.get(`/chat/sessions/${encodeURIComponent(String(sessionId))}/messages`, {
    params: { teamId: tid, ...params },
  });
  return data;
}

export async function postChatMessage(sessionId, teamId, payload) {
  const tid = assertTeamId(teamId);
  const { data } = await axiosInstance.post(
    `/chat/sessions/${encodeURIComponent(String(sessionId))}/messages`,
    { teamId: tid, ...payload }
  );
  return data;
}

export async function patchChatSession(sessionId, teamId, body) {
  const tid = assertTeamId(teamId);
  const { data } = await axiosInstance.patch(
    `/chat/sessions/${encodeURIComponent(String(sessionId))}`,
    { teamId: tid, ...body }
  );
  return data;
}

export async function archiveChatSession(sessionId, teamId) {
  const tid = assertTeamId(teamId);
  const { data } = await axiosInstance.delete(
    `/chat/sessions/${encodeURIComponent(String(sessionId))}`,
    { params: { teamId: tid } }
  );
  return data;
}

/**
 * Layers 2–4 full investigation — plan + execute all phase1_tools in parallel.
 *
 * Returns (among others):
 *   finalAnswer, planHistory, resultsPerIteration, evaluatorScores, stopReason, cyclesUsed, maxIterations
 *
 * status values:
 *   "complete"                — plan → tools (if configured) → evaluate loop finished; finalAnswer set
 *   "complete_no_orchestrator"— same but tools were not run (orchestrator missing)
 *   "clarify"                 — planner asked for clarification (no tool execution)
 *   (legacy) "proceed", "no_tools", "plan_only" may still appear from older servers
 *
 * @param {number} teamId
 * @param {{ query: string, sessionId?: string, attachments?: object[] }} body
 */
export async function postChatInvestigate(teamId, body = {}) {
  const tid = assertTeamId(teamId);
  const { data } = await axiosInstance.post('/chat/investigate', {
    teamId: tid,
    ...body,
  });
  return data;
}

export default {
  listChatSessions,
  createChatSession,
  getCurrentChatSession,
  getChatMessages,
  postChatMessage,
  patchChatSession,
  archiveChatSession,
  postChatInvestigate,
};
